import {switchMap, delay} from "rxjs/operators";
import {ofType} from "redux-observable";

import * as actionTypes from "../constants/actionTypes";
import * as methods from "../constants/methods";
import * as config from "../constants/config";
import * as orderStatus from "../constants/orderStatus";
import * as componentTypes from "../constants/componentTypes";
import * as handlers from "../constants/handlers";
import * as globalActions from "../actions/globalActions";
import * as navigationActions from "../actions/navigationActions";
import * as validationHelper from "../helpers/validationHelper";
import * as messageHelper from "../helpers/messageHelper";
import * as windowService from "../services/windowService";

const loginSuccess = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.LOG_IN_SUCCESS),
        switchMap(({payload}) => {
            let loginSuccessActions = [];

            if (state$.value.module.setupSoftware) {
                loginSuccessActions.push({
                    type: actionTypes.SETUP_UPDATE_PRIVILEGES,
                    payload: {
                        loggedUser: state$.value.login.loggedUser
                    }
                });
            }

            if (
                payload.loggedUser.currency !== null &&
                !validationHelper.isCurrencyCodeValid(payload.loggedUser.currency.code)
            ) {
                loginSuccessActions.push(messageHelper.getErrorMessageAction("flashMessage.invalidCurrency"));
            }

            loginSuccessActions.push({type: actionTypes.GLOBAL_STORE_INIT});

            return loginSuccessActions;
        })
    );

const changeUserSuccess = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.CHANGE_USER_SUCCESS),
        switchMap(({payload}) => {
            let changeUserSuccessActions = [];

            if (state$.value.module.setupSoftware) {
                changeUserSuccessActions.push({
                    type: actionTypes.SETUP_UPDATE_PRIVILEGES,
                    payload: {
                        loggedUser: state$.value.login.loggedUser
                    }
                });
            }

            if (
                payload.loggedUser.currency !== null &&
                !validationHelper.isCurrencyCodeValid(payload.loggedUser.currency.code)
            ) {
                changeUserSuccessActions.push(messageHelper.getErrorMessageAction("flashMessage.invalidCurrency"));
            }

            changeUserSuccessActions.push({
                type: actionTypes.MENU_TRANSLATE_KEYWORDS
            });

            return changeUserSuccessActions;
        })
    );

const globalStoreInit = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.GLOBAL_STORE_INIT),
        delay(500),
        switchMap(() => {
            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_LAST_DISPENSED_FORMULAS,
                        params: {
                            rowCount: config.MAX_LAST_DISPENSED_LIMIT,
                            offset: 0
                        }
                    },
                    method: handlers.INIT_GLOBAL_LAST_DISPENSED_FORMULAS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_FAVORITE_FORMULAS
                    },
                    method: handlers.INIT_GLOBAL_FAVORITE_FORMULAS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_COLORANTS,
                        params: {
                            rowCount: -1,
                            offset: 0
                        }
                    },
                    method: handlers.INIT_GLOBAL_COLORANTS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_PRODUCTS,
                        params: {
                            tree: true
                        }
                    },
                    method: handlers.INIT_GLOBAL_ALL_PRODUCTS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_PRODUCTS,
                        params: {
                            tree: false
                        }
                    },
                    method: handlers.INIT_GLOBAL_ALL_PRODUCTS_SIMPLE,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_FANDECKS
                    },
                    method: handlers.INIT_GLOBAL_ALL_FANDECKS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_MARGINS
                    },
                    method: handlers.INIT_GLOBAL_MARGINS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_DATABASES_LIST
                    },
                    method: handlers.INIT_GLOBAL_DATABASES_LIST,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_AVAILABLE_SERVERS,
                        params: {
                            compatibleOnly: true,
                            activeOnly: true,
                            orderingOnly: true
                        }
                    },
                    method: handlers.INIT_GLOBAL_COMPATIBLE_SERVERS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_INITIAL_DATA
                    },
                    method: handlers.INIT_GLOBAL_INITIAL_DATA,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_SETTING_OPTIONS
                    },
                    method: handlers.INIT_GLOBAL_SETTING_OPTIONS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_USER_SETTINGS
                    },
                    method: handlers.INIT_GLOBAL_USER_SETTINGS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_PRICE_SETTINGS
                    },
                    method: handlers.INIT_GLOBAL_PRICE_SETTINGS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_SERVER_CONFIGURATION
                    },
                    method: handlers.INIT_GLOBAL_SERVER_CONFIGURATION,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_SERVER_COLORANTS
                    },
                    method: handlers.INIT_GLOBAL_ALL_SERVER_COLORANTS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_TO_DISPENSE_GET_ORDERS,
                        params: {
                            availableOnly: state$.value.module.weberSoftware,
                            offset: 0,
                            rowCount: config.MAX_ORDERS_LIMIT,
                            statusFilter: orderStatus.TO_DISPENSE_ORDER_STATUS_LIST
                        }
                    },
                    method: handlers.INIT_GLOBAL_TO_DISPENSE_GET_ORDERS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_UNITS
                    },
                    method: handlers.INIT_GLOBAL_GET_ALL_UNITS,
                    tabId: 0
                }
            ];
        })
    );

const increaseSoftwareLoading = (action$, state$) =>
    action$.pipe(
        ofType(
            actionTypes.INCREASE_LOADING,
            actionTypes.RCC_INITIALIZED,
            actionTypes.DRIVERS_INITIALIZED,
            actionTypes.MACHINE_INITIALIZED,
            actionTypes.PRINTER_INITIALIZED,
            actionTypes.SPECTRO_INITIALIZED
        ),
        switchMap(() => {
            return [
                {
                    type: actionTypes.PROCESS_LOADING,
                    payload: {
                        loading: state$.value.global.loading
                    }
                }
            ];
        })
    );

const processSoftwareLoading = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.PROCESS_LOADING),
        delay(1000),
        switchMap(({payload}) => {
            if (state$.value.global.initialized || payload.loading < config.SERVER_INIT) {
                return [];
            }

            if (payload.loading === config.SERVER_INIT) {
                if (state$.value.module.rccModule && !state$.value.rcc.connected && state$.value.rcc.url !== null) {
                    return [
                        {
                            type: actionTypes.RCC_CONNECT,
                            payload: {
                                url: config.WEBSOCKET_PROTOCOL + state$.value.rcc.url + ":" + config.RCC_PORT
                            }
                        }
                    ];
                } else {
                    return [
                        {
                            type: actionTypes.SOFTWARE_INITIALIZED
                        }
                    ];
                }
            }

            if (payload.loading < config.INITIALIZATION_NUMBER) {
                return [];
            }

            return [
                {
                    type: actionTypes.SOFTWARE_INITIALIZED
                }
            ];
        })
    );

const globalStoreReload = (action$) =>
    action$.pipe(
        ofType(actionTypes.GLOBAL_STORE_RELOAD),
        switchMap(() => {
            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_COLORANTS
                    },
                    method: handlers.RELOAD_ALL_COLORANTS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_PRODUCTS,
                        params: {
                            tree: true
                        }
                    },
                    method: handlers.RELOAD_GLOBAL_ALL_PRODUCTS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_PRODUCTS,
                        params: {
                            tree: false
                        }
                    },
                    method: handlers.RELOAD_GLOBAL_ALL_PRODUCTS_SIMPLE,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_FANDECKS
                    },
                    method: handlers.RELOAD_GLOBAL_ALL_FANDECKS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_MARGINS
                    },
                    method: handlers.GET_ALL_MARGINS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_INITIAL_DATA
                    },
                    method: handlers.RELOAD_GLOBAL_INITIAL_DATA,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_USER_SETTINGS
                    },
                    method: handlers.RELOAD_USER_SETTINGS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_PRICE_SETTINGS
                    },
                    method: handlers.RELOAD_GLOBAL_PRICE_SETTINGS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_SERVER_COLORANTS
                    },
                    method: handlers.RELOAD_ALL_SERVER_COLORANTS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_UNITS
                    },
                    method: handlers.RELOAD_GLOBAL_GET_ALL_UNITS,
                    tabId: 0
                }
            ];
        })
    );

const loadSelectedUserGroupList = (action$) =>
    action$.pipe(
        ofType(actionTypes.LOAD_SELECTED_USER_GROUP_LIST),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.SAVE_ACTIVE_USER,
                    payload: {
                        userId: payload.userId,
                        isLocal: payload.isLocal
                    }
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_SELECTED_USER_GROUPS,
                        params: {
                            userId: payload.userId,
                            isLocal: payload.isLocal
                        }
                    },
                    method: handlers.GET_SELECTED_USER_GROUPS,
                    tabId: 0
                }
            ];
        })
    );

const deleteConfirmation = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.DELETE_ITEM_CONFIRMATION),
        switchMap(({payload}) => {
            if (payload.next === undefined) {
                return [];
            }

            if (payload.next && windowService.getIgnoredConfirmations().includes(payload.next.type)) {
                return [payload.next];
            }

            if (
                state$.value.login.loggedIn &&
                state$.value.global.initialized &&
                state$.value.settings.deleteConfirmation
            ) {
                return [
                    {
                        type: actionTypes.NAVIGATION_OPEN_MODAL,
                        payload: {
                            tabId: payload.tabId,
                            type: componentTypes.CONFIRMATION_MODAL,
                            params: {
                                next: payload.next,
                                dialog: payload.dialog,
                                confirmBtn: null
                            }
                        }
                    }
                ];
            }

            return [payload.next];
        })
    );

const userAuthentication = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.USER_AUTHENTICATION),
        switchMap(({payload}) => {
            if (payload.next === undefined) {
                return [];
            }

            if (state$.value.settings.userIdentificationEnabled) {
                return [
                    {
                        type: actionTypes.NAVIGATION_OPEN_MODAL,
                        payload: {
                            tabId: payload.tabId,
                            type: componentTypes.AUTHENTICATION_MODAL,
                            params: {
                                next: payload.next
                            }
                        }
                    }
                ];
            }

            return [payload.next];
        })
    );

const saveTintometricSystem = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_TINTOMETRIC_SYSTEM),
        switchMap(() => {
            if (
                state$.value.global.tintometricSystem === null ||
                window.imageStore.getTsImageById(state$.value.global.tintometricSystem.id) !== null
            ) {
                return [];
            }

            return [globalActions.getTintometricSystemIcon(state$.value.global.tintometricSystem.id)];
        })
    );

const savePartialUpdates = (action$) =>
    action$.pipe(
        ofType(actionTypes.PARTIAL_UPDATES_SAVE_DATA),
        switchMap(() => {
            return [
                navigationActions.openJustOneModal(
                    componentTypes.MODAL_SETTINGS_TAB,
                    componentTypes.PARTIAL_UPDATE_MODAL
                ),
                {
                    type: actionTypes.SET_BLOCKED_UI,
                    payload: {
                        blocked: true
                    }
                }
            ];
        })
    );

export default [
    loginSuccess,
    changeUserSuccess,
    globalStoreInit,
    increaseSoftwareLoading,
    processSoftwareLoading,
    globalStoreReload,
    loadSelectedUserGroupList,
    deleteConfirmation,
    userAuthentication,
    saveTintometricSystem,
    savePartialUpdates
];
